/**
 * RTProposalsReport — 25/02/2026
 *
 * Painel que mostra o breakdown de RTs realizadas + Propostas enviadas
 * por Dia / Semana / Mês + ranking por Consultor(a). Botão de exportação
 * para PDF chama `/api/reports/rt-proposals/pdf`.
 */

import { useEffect, useMemo, useState } from "react";
import { Loader2, FileDown, BarChart3, TrendingUp, Users, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmt = (n) => (n || 0).toLocaleString("pt-PT");

const TABS = [
  { key: "day",   label: "Por dia" },
  { key: "week",  label: "Por semana" },
  { key: "month", label: "Por mês" },
];

const labelFor = (key, tab) => {
  if (tab === "month") {
    const [y, m] = key.split("-");
    const dt = new Date(Number(y), Number(m) - 1, 1);
    return dt.toLocaleDateString("pt-PT", { month: "short", year: "numeric" });
  }
  if (tab === "week") return key.replace(/^(\d{4})-W(\d{2})$/, "Sem. $2/$1");
  const dt = new Date(key);
  return dt.toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
};

export default function RTProposalsReport() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("week");
  const [exporting, setExporting] = useState(false);
  const [consultorId, setConsultorId] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const q = consultorId ? `?consultor_id=${encodeURIComponent(consultorId)}` : "";
        const r = await api.get(`/reports/rt-proposals${q}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [consultorId]);

  const series = useMemo(() => {
    if (!data) return [];
    if (tab === "day") return data.by_day || [];
    if (tab === "week") return data.by_week || [];
    return data.by_month || [];
  }, [data, tab]);

  const maxVal = useMemo(() => {
    return series.reduce((m, s) => Math.max(m, s.rt || 0, s.propostas || 0), 1);
  }, [series]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const q = consultorId ? `?consultor_id=${encodeURIComponent(consultorId)}` : "";
      const r = await api.get(`/reports/rt-proposals/pdf${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      const suffix = consultorId ? `-${consultorId.slice(0, 8)}` : "";
      a.download = `Caring-RT-Propostas-${stamp}${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF gerado");
    } catch (e) {
      toast.error("Falha ao gerar PDF");
    } finally {
      setExporting(false);
    }
  };

  if (err) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 text-sm text-muted-foreground" data-testid="rtprop-report-error">
        Não foi possível carregar o relatório.
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-2 text-sm text-muted-foreground" data-testid="rtprop-report-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar relatório…
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 mb-8" data-testid="rtprop-report">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-foreground" />
          </div>
          <div>
            <h3 className="font-ui text-lg text-foreground">Relatório · RTs realizadas e Propostas enviadas</h3>
            <div className="text-xs text-muted-foreground">Últimos 12 meses · quebras por dia, semana, mês e consultor</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(data.consultors || []).length > 0 && (
            <div className="flex items-center gap-1.5" data-testid="rtprop-consultor-filter">
              <Users className="w-3.5 h-3.5 text-muted-foreground" />
              <select
                value={consultorId}
                onChange={(e) => setConsultorId(e.target.value)}
                className="text-xs bg-background border border-border rounded-full px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-foreground/20"
                data-testid="rtprop-consultor-select"
                title="Filtrar por consultor"
              >
                <option value="">Todos os consultores</option>
                {(data.consultors || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {consultorId && (
                <button
                  type="button"
                  onClick={() => setConsultorId("")}
                  className="text-muted-foreground hover:text-foreground p-0.5 rounded"
                  title="Limpar filtro"
                  data-testid="rtprop-consultor-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={exportPdf}
            disabled={exporting}
            data-testid="rtprop-export-pdf"
            className="inline-flex items-center gap-2 bg-foreground text-background hover:bg-foreground/90 rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-50 transition-colors"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-[#256D8A]/30 bg-[#256D8A]/5 p-4" data-testid="rtprop-total-rt">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#256D8A] font-semibold">RTs realizadas (12 meses)</div>
          <div className="font-ui text-3xl mt-1 tabular-nums text-[#256D8A]">{fmt(data.totals?.rt)}</div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning-bg))]/50 p-4" data-testid="rtprop-total-propostas">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--warning))] font-semibold">Propostas enviadas (12 meses)</div>
          <div className="font-ui text-3xl mt-1 tabular-nums text-[hsl(var(--warning))]">{fmt(data.totals?.propostas)}</div>
        </div>
        {/* 26/02/2026 — Métrica de saúde da comunicação. >15% = vermelho
            (sinal de que os emails de lembrete são intrusivos e o
            cliente pediu para parar). Rácio calculado sobre propostas
            enviadas. */}
        {(() => {
          const oo = data.totals?.optouts ?? 0;
          const pct = data.totals?.optout_pct ?? 0;
          const critical = pct >= 15;
          const tone = critical
            ? { border: "border-[hsl(var(--danger))]/40", bg: "bg-[hsl(var(--danger-bg))]/50", text: "text-[hsl(var(--danger))]" }
            : { border: "border-border", bg: "bg-muted/30", text: "text-foreground" };
          return (
            <div className={`rounded-xl border ${tone.border} ${tone.bg} p-4`} data-testid="rtprop-total-optouts">
              <div className={`text-[10px] uppercase tracking-[0.22em] ${tone.text} font-semibold flex items-center gap-1.5`}>
                Opt-outs (12 meses)
                {critical && <span title="Volume elevado — reveja a agressividade dos lembretes" className="text-[10px]">·⚠️</span>}
              </div>
              <div className={`font-ui text-3xl mt-1 tabular-nums ${tone.text}`}>
                {fmt(oo)}
                <span className="text-sm text-muted-foreground ml-2 font-normal">
                  ({pct.toFixed(1).replace(".", ",")}%)
                </span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 text-[11px]" data-testid="rtprop-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`rtprop-tab-${t.key}`}
            className={`px-3 py-1.5 rounded-full border transition-colors ${
              tab === t.key
                ? "bg-foreground text-background border-foreground"
                : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Mini bar chart via CSS */}
      <div className="rounded-lg border border-border/60 p-4 mb-6" data-testid="rtprop-chart">
        <div className="flex items-end gap-2 h-32 overflow-x-auto">
          {series.map((s) => {
            const rtHeight = Math.round(((s.rt || 0) / maxVal) * 100);
            const propHeight = Math.round(((s.propostas || 0) / maxVal) * 100);
            return (
              <div key={s.key} className="flex flex-col items-center gap-1 shrink-0" title={`${labelFor(s.key, tab)}\nRT: ${s.rt} · Propostas: ${s.propostas}`}>
                <div className="flex items-end gap-0.5 h-24">
                  <div className="w-4 rounded-sm bg-[#256D8A]" style={{ height: `${rtHeight}%` }} />
                  <div className="w-4 rounded-sm bg-[hsl(var(--warning))]" style={{ height: `${propHeight}%` }} />
                </div>
                <div className="text-[9px] text-muted-foreground text-center whitespace-nowrap max-w-[64px] truncate">
                  {labelFor(s.key, tab)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 bg-[#256D8A] rounded-sm" /> RTs realizadas
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 bg-[hsl(var(--warning))] rounded-sm" /> Propostas enviadas
          </span>
        </div>
      </div>

      {/* Consultor ranking */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" /> Ranking por Consultor(a)
          </div>
          <div className="text-[10px] text-muted-foreground/70">
            Conversão = Propostas ÷ RTs
          </div>
        </div>
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <table className="w-full text-sm" data-testid="rtprop-consultor-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold bg-muted/30 border-b border-border">
                <th className="text-left py-2.5 px-3 w-10">#</th>
                <th className="text-left py-2.5 px-3">Consultor(a)</th>
                <th className="text-right py-2.5 px-3">RTs</th>
                <th className="text-right py-2.5 px-3">Propostas</th>
                <th className="text-right py-2.5 px-3">Conversão</th>
                <th className="text-right py-2.5 px-3" title="Clientes que pediram para não receber mais lembretes">Opt-outs</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_consultor || []).slice(0, 20).map((r, i) => {
                const ooPct = r.optout_pct ?? 0;
                const ooCritical = ooPct >= 15;
                return (
                  <tr key={r.consultor_id || `_${i}`} className="border-b border-border/60 last:border-b-0 hover:bg-muted/30" data-testid={`rtprop-consultor-row-${i}`}>
                    <td className="py-2 px-3 text-muted-foreground tabular-nums">{i + 1}</td>
                    <td className="py-2 px-3 text-sm text-foreground">{r.consultor_name}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-[#256D8A] font-semibold">{fmt(r.rt)}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-[hsl(var(--warning))] font-semibold">{fmt(r.propostas)}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                      {r.rt > 0 ? `${(r.conversion || 0).toFixed(1).replace(".", ",")}%` : "—"}
                    </td>
                    <td className={`py-2 px-3 text-right tabular-nums font-semibold ${ooCritical ? "text-[hsl(var(--danger))]" : "text-muted-foreground"}`}
                        data-testid={`rtprop-consultor-optout-${i}`}
                        title={ooCritical ? "Volume elevado (>=15%) — reveja a comunicação" : undefined}>
                      {r.propostas > 0
                        ? `${fmt(r.optouts || 0)} (${ooPct.toFixed(1).replace(".", ",")}%)`
                        : "—"}
                    </td>
                  </tr>
                );
              })}
              {(data.by_consultor || []).length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">Sem actividade no período.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
